export interface ColumnBlockInsert {
  /** The block members that actually exist in `order`, normalised to display order. */
  block: string[]
  /** `order` with every block member removed. */
  withoutBlock: string[]
  /** Index into `withoutBlock` at which the block should be spliced back in. */
  insertIndex: number
}

/**
 * Normalises a set of column ids to the order they appear in, dropping anything unknown.
 *
 * Selections are built click-by-click (Ctrl/Cmd+click appends), so the raw id list can be in any
 * order. A block move must preserve the columns' existing order relative to one another, which
 * means resolving them against the display order before they are moved.
 */
const orderBlock = (order: string[], block: string[]): string[] => {
  const blockIds = new Set(block)
  return order.filter((id) => blockIds.has(id))
}

/**
 * Works out where a dragged block of columns lands when it is dropped on `overId`.
 *
 * Matches the single-column drop convention: dragging towards the end of the order inserts *after*
 * the hovered column, dragging towards the start inserts *before* it.
 *
 * Shared by the Column Order menu (vertical list) and the grid header drag-and-drop so both agree
 * on where a block lands.
 */
export const computeInsertIndex = (order: string[], block: string[], overId: string): ColumnBlockInsert => {
  const orderedBlock = orderBlock(order, block)
  const blockIds = new Set(orderedBlock)
  const withoutBlock = order.filter((id) => !blockIds.has(id))

  const activeIndex = order.indexOf(orderedBlock[0]) // first item of the dragged block
  const overOriginalIndex = order.indexOf(overId) // index of the hovered column in the *original* order
  const overIndex = withoutBlock.indexOf(overId) // …and after removing the block

  // moving towards the end ⇢ insert *after* the hovered column
  const isMovingForwards = overOriginalIndex > activeIndex
  const insertIndex = Math.min(
    isMovingForwards ? overIndex + 1 : overIndex,
    withoutBlock.length // safeguard: never past the end
  )

  return { block: orderedBlock, withoutBlock, insertIndex }
}

/**
 * Returns `order` with `block` moved to the drop position, as one contiguous run in its original
 * relative order. A non-contiguous block collapses together and the columns it left behind close up.
 *
 * Returns `order` unchanged when there is nothing to move, when the drop target is part of the
 * block, or when the drop target isn't in the order at all.
 */
export const moveColumnBlock = (order: string[], block: string[], overId: string): string[] => {
  const { block: orderedBlock, withoutBlock, insertIndex } = computeInsertIndex(order, block, overId)
  if (orderedBlock.length === 0 || orderedBlock.includes(overId) || !order.includes(overId)) return order

  return [...withoutBlock.slice(0, insertIndex), ...orderedBlock, ...withoutBlock.slice(insertIndex)]
}

/**
 * The columns that travel with a drag: the whole multi-column selection when the dragged header is
 * part of it, otherwise just the dragged column.
 *
 * Grouped columns are excluded — the grid pins them to the front of the header row and marks them
 * undraggable, so they stay where they are rather than blocking the rest of the block from moving.
 */
export const resolveDraggedColumnBlock = (
  draggedColumnId: string,
  selectedColumnIds: string[],
  groupedColumnIds: string[]
): string[] => {
  if (!draggedColumnId) return []
  const grouped = new Set(groupedColumnIds)
  if (!selectedColumnIds.includes(draggedColumnId)) return grouped.has(draggedColumnId) ? [] : [draggedColumnId]
  return selectedColumnIds.filter((id) => !grouped.has(id))
}

export interface ColumnBlockRect {
  left: number
  width: number
}

/**
 * Where a dragged block would sit if it were dropped right now, in pixels — the geometry behind the
 * drop-preview ghost.
 *
 * `columns` are the rendered columns in display order, and `startLeft` is the offset the first of
 * them sits at (the row-number cell, plus the tree column when there is one). `overId` is the header
 * currently under the cursor, or '' when the drag hasn't reached one.
 *
 * Returns one rect per contiguous run of the block. A drop that would actually move something always
 * collapses the block into a single run, so that case is one rect; a drag that hasn't left the block
 * yet marks the columns where they already are, which is what puts the ghost on screen from the
 * moment the drag starts. An empty block has no rects.
 */
export const computeColumnBlockDropRects = (
  columns: readonly { publicId: string; width: number }[],
  block: string[],
  overId: string,
  startLeft: number
): ColumnBlockRect[] => {
  const order = columns.map((column) => column.publicId)
  const { block: orderedBlock } = computeInsertIndex(order, block, overId)
  if (orderedBlock.length === 0) return []

  // moveColumnBlock returns the order untouched while the cursor is still inside the block (or hasn't
  // reached a column at all), so this covers both the moved and the not-yet-moved case.
  const landedOrder = moveColumnBlock(order, orderedBlock, overId)
  const widthsById = new Map(columns.map((column) => [column.publicId, column.width]))
  const blockIds = new Set(orderedBlock)

  const rects: ColumnBlockRect[] = []
  let left = startLeft
  let run: ColumnBlockRect | null = null
  for (const id of landedOrder) {
    const width = widthsById.get(id) ?? 0
    if (!blockIds.has(id)) {
      run = null
    } else if (run) {
      run.width += width
    } else {
      run = { left, width }
      rects.push(run)
    }
    left += width
  }

  return rects
}
